import { useEffect, useRef, useState } from "react";
import { API, getToken, getGuestSession, downloadCvDocx } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ElitePicks } from "@/components/chat/ElitePicks";
import { SchoolPicker } from "@/components/chat/SchoolPicker";
import { Markdown } from "@/components/chat/Markdown";
import { toast } from "sonner";
import { GraduationCap, Rocket, Send, Sparkles, Mail, Compass, Gauge, FileText, Download } from "lucide-react";

const PERSONA_META = {
  undergraduate: { name: "Elite Undergraduate", icon: GraduationCap, tagline: "Your bachelor & discovery advisor" },
  postgraduate: { name: "Elite Postgraduate", icon: Rocket, tagline: "Your master's, MBA & PhD strategist" },
  cover_letter: { name: "Cover Letter Coach", icon: Mail, tagline: "Craft your motivation letter, step by step" },
  orientation: { name: "Orientation Coach", icon: Compass, tagline: "Discover the studies that fit you" },
  predictor: { name: "Admission Predictor", icon: Gauge, tagline: "Estimate your chances & how to improve" },
  cv: { name: "CV Builder", icon: FileText, tagline: "Build your CV in conversation — downloadable as Word" },
};

const STARTERS = {
  undergraduate: [
    "Which bachelor should I pick for business abroad?",
    "How do I get into a top UK university?",
    "What scholarships can I apply for?",
  ],
  postgraduate: [
    "How competitive is an MBA at INSEAD?",
    "Improve my chances for a master's in AI",
    "What GMAT score do I need for HEC?",
  ],
  cover_letter: [
    "Help me write a cover letter for MSc Finance at HEC",
    "I'm applying to LSE — draft my motivation letter",
    "Improve the intro of my cover letter",
  ],
  orientation: [
    "I don't know what to study — help me figure it out",
    "I like maths and design, what fits me?",
    "Guide me through a quick orientation test",
  ],
  predictor: [
    "What are my chances for an MBA at INSEAD?",
    "Estimate my admission odds for MSc Finance at HEC",
    "How can I improve my application?",
  ],
  cv: [
    "Help me build my CV for a master's application",
    "I want an ATS-friendly academic CV",
    "Let's start my CV — I'll give you my details",
  ],
};

// Strip the CV builder's custom tags so the chat stays readable; a download
// card is shown instead. Also hides an unclosed tag block while streaming.
function cleanCvTags(text) {
  if (!text) return text;
  let t = text
    .replace(/<titre-cv>[\s\S]*?<\/titre-cv>/gi, "")
    .replace(/<contenu-cv>[\s\S]*?<\/contenu-cv>/gi, "")
    .replace(/<titre-lettre>[\s\S]*?<\/titre-lettre>/gi, "")
    .replace(/<contenu-lettre>[\s\S]*?<\/contenu-lettre>/gi, "")
    .replace(/<titre-bilan>[\s\S]*?<\/titre-bilan>/gi, "")
    .replace(/<contenu-bilan>[\s\S]*?<\/contenu-bilan>/gi, "")
    .replace(/<options>[\s\S]*?<\/options>/gi, "")
    .replace(/<titre-conversation>[\s\S]*?<\/titre-conversation>/gi, "");
  const openIdx = t.search(/<(titre-cv|contenu-cv|titre-lettre|contenu-lettre|titre-bilan|contenu-bilan|options|titre-conversation)>/i);
  if (openIdx !== -1) t = t.slice(0, openIdx);
  return t.replace(/<[a-z-]*$/i, "").trim();
}

// Orientation only: parse "<options>A | B | C</options>" into clickable chips.
function parseOptions(text) {
  if (!text) return [];
  const m = /<options>([\s\S]*?)<\/options>/i.exec(text);
  if (!m) return [];
  return m[1].split("|").map((s) => s.trim()).filter(Boolean).slice(0, 6);
}

function predictorStarters(f) {
  const name = f.item_type === "program" && f.school ? `${f.title} at ${f.school}` : f.title;
  return [
    `What are my chances of getting into ${name}?`,
    `What should I improve to get into ${f.title}?`,
    `What are the admission requirements and deadlines for ${f.title}?`,
    `Is ${f.title} a Reach, Target or Safety for my profile?`,
  ];
}

function focusStarters(f) {
  const name = f.item_type === "program" && f.school ? `${f.title} at ${f.school}` : f.title;
  return [
    `Is ${name} a good fit for my profile?`,
    `What do I need to get admitted to ${f.title}?`,
    `What are the tuition fees and funding options for ${f.title}?`,
    `Which careers and outcomes can I expect after ${f.title}?`,
  ];
}

function FocusBanner({ focus }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-orange/30 bg-orange/5 p-3 mb-2 text-left w-full max-w-sm" data-testid="chat-focus-banner">
      <div className="h-10 w-10 rounded-xl bg-orange/15 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-orange" /></div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-orange">{focus.item_type === "program" ? "Program" : "School"} in focus</div>
        <div className="text-sm font-bold text-navy truncate">{focus.title}</div>
        {focus.subtitle && <div className="text-xs text-muted-foreground truncate">{focus.subtitle}</div>}
      </div>
    </div>
  );
}

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function ChatInterface({ persona, agent, isGuest, utmSource, focus: focusProp, initialMessages = [], conversationId: initialConvId, onLimitReached, onFirstMessage, bare = false }) {
  const [picked, setPicked] = useState(null); // predictor: school item | "none" | null
  const focus = focusProp || (picked && picked !== "none" ? picked : null);
  const [messages, setMessages] = useState(() => initialMessages.map((m, i) => ({ ...m, id: m.id || `init-${i}` })));
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [convId, setConvId] = useState(initialConvId || null);
  const scrollRef = useRef(null);
  const metaKey = agent || persona;
  const meta = PERSONA_META[metaKey] || PERSONA_META.undergraduate;
  const Icon = meta.icon;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content, id: uid() }, { role: "assistant", content: "", id: uid() }]);
    setStreaming(true);
    onFirstMessage?.();

    const token = getToken();
    const payload = { message: content, persona, conversation_id: convId };
    if (agent) payload.agent = agent;
    if (isGuest) payload.session_id = getGuestSession();
    if (isGuest && utmSource) payload.utm_source = utmSource;
    if (focus && !convId) payload.focus = focus.external_id;

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("stream unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === "meta") {
            if (evt.conversation_id) setConvId(evt.conversation_id);
          } else if (evt.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: last.content + evt.content };
              return copy;
            });
          } else if (evt.type === "limit") {
            setMessages((m) => m.slice(0, -1)); // drop empty assistant placeholder
            setStreaming(false);
            onLimitReached?.();
            return;
          } else if (evt.type === "cv") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, cvDoc: { id: evt.doc_id, title: evt.title, format: evt.format } };
              return copy;
            });
          } else if (evt.type === "links") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], links: evt.items };
              return copy;
            });
          } else if (evt.type === "title") {
            onFirstMessage?.();
          } else if (evt.type === "error") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: evt.content };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, content: "Connection lost. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className={`flex flex-col h-full bg-white overflow-hidden ${bare ? "" : "rounded-3xl border border-border shadow-card"}`} data-testid="chat-interface">
      {/* header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-navy text-white">
        <div className="h-10 w-10 rounded-xl bg-orange flex items-center justify-center">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-display font-extrabold italic uppercase tracking-wide">{meta.name}</div>
          <div className="text-xs text-white/60">{meta.tagline}</div>
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-white/70">
          <span className="h-2 w-2 rounded-full bg-green-400 typing-dot" /> Online
        </span>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 bg-[hsl(210_20%_98%)]">
       <div className="max-w-3xl mx-auto w-full min-h-full space-y-4 pb-10">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="h-16 w-16 rounded-2xl bg-orange/15 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-orange" />
            </div>
            <h3 className="font-display font-black italic text-xl text-navy uppercase">Ask {meta.name}</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              {focus ? (isGuest ? "Pick a question to start." : "Pick a question to start — the advisor already knows this page and your profile.") : "Get personalized guidance on schools, programs and your application."}
            </p>
            {metaKey === "predictor" && !focusProp && picked === null ? (
              <SchoolPicker canFav={!isGuest} onPick={setPicked} onSkip={() => setPicked("none")} />
            ) : (
            <div className="flex flex-col gap-2 mt-5 w-full max-w-sm items-center">
              {focus && <FocusBanner focus={focus} />}
              {(focus ? (metaKey === "predictor" ? predictorStarters(focus) : focusStarters(focus)) : STARTERS[metaKey] || STARTERS.undergraduate).map((s, i) => (
                <button
                  key={s}
                  data-testid={`chat-starter-${i}`}
                  onClick={() => send(s)}
                  className="w-full text-left text-sm px-4 py-2.5 rounded-xl border border-border bg-white hover:border-orange hover:bg-orange/5 transition-colors"
                >
                  {s}
                </button>
              ))}
              {metaKey === "predictor" && picked !== null && (
                <button onClick={() => setPicked(null)} data-testid="predictor-change-school" className="text-xs font-semibold text-orange hover:underline mt-1">
                  {picked === "none" ? "Choose a school instead" : "Change school"}
                </button>
              )}
            </div>
            )}
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${
              m.role === "user"
                ? "bg-navy text-white rounded-br-md"
                : "bg-white border border-border rounded-bl-md shadow-sm"
            }`}>
              {m.role === "user" ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
              ) : m.content === "" && streaming ? (
                <span className="flex gap-1 py-1">
                  <span className="h-2 w-2 rounded-full bg-orange typing-dot" style={{ animationDelay: "0s" }} />
                  <span className="h-2 w-2 rounded-full bg-orange typing-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="h-2 w-2 rounded-full bg-orange typing-dot" style={{ animationDelay: "0.4s" }} />
                </span>
              ) : (
                <>
                  <Markdown>{cleanCvTags(m.content)}</Markdown>
                  {(m.cvDoc || m.cv_doc) && (() => { const cv = m.cvDoc || m.cv_doc; return (
                    <div className="mt-3 flex items-center gap-3 rounded-2xl border border-orange/30 bg-orange/5 p-3" data-testid="cv-download-card">
                      <div className="h-10 w-10 rounded-xl bg-orange/15 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-orange" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-navy truncate">{cv.title}</div>
                        <div className="text-[11px] text-muted-foreground">Saved to My Documents</div>
                      </div>
                      <Button
                        size="sm"
                        data-testid={`cv-download-${cv.id}`}
                        onClick={async () => {
                          try { await downloadCvDocx(cv.id, cv.title, cv.format); }
                          catch { toast.error("Download failed — please try again."); }
                        }}
                        className="rounded-full bg-navy hover:bg-navy-deep text-white shrink-0"
                      >
                        <Download className="h-3.5 w-3.5 mr-1" /> {cv.format === "pdf" ? "PDF" : "Word"}
                      </Button>
                    </div>
                  ); })()}
                  <ElitePicks items={m.links} canFav={!isGuest} />
                  {metaKey === "orientation" && idx === messages.length - 1 && !streaming && (() => {
                    const opts = parseOptions(m.content);
                    if (!opts.length) return null;
                    return (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="orientation-options">
                        {opts.map((opt, i) => (
                          <button
                            key={i}
                            data-testid={`orientation-option-${i}`}
                            onClick={() => send(opt)}
                            className="text-sm px-3 py-1.5 rounded-full border border-orange/40 bg-orange/5 text-navy font-medium hover:bg-orange hover:text-white hover:border-orange transition-colors"
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        ))}
       </div>
      </div>

      {/* input */}
      <div className="p-4 border-t border-border bg-white">
        <div className="flex items-end gap-2 max-w-3xl mx-auto w-full">
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Message ${meta.name}...`}
            rows={1}
            className="resize-none min-h-[48px] max-h-32 rounded-2xl border-border focus-visible:ring-orange"
          />
          <Button
            data-testid="chat-send-btn"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="h-12 w-12 shrink-0 rounded-full bg-orange hover:bg-orange-dark text-white"
          >
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
